import { GameScene } from '../GameScene';
import { NpcInfoPanel } from '../ui/NpcInfoPanel';
import { buildNpcDisplayInfo, getNPCTemplate } from '@shared/data/npcs';
import { ClientMessageType, type InteractNpcMessage } from '@shared/types/messages';
import type { NPCStateView } from '../../state/GameStateView';

/**
 * Panel de info de NPC (right-click sobre un NPC): abre/crea el panel con vida/daño/defensas/Reino.
 * Extraído de GameScene (god-object). El estado (npcInfoPanel) vive en GameScene (host), accedido vía
 * `this.scene.*`. Ver docs/VENDORS_AND_NPC_INTERACTION.md.
 */
export class NpcInfoController {
  constructor(private scene: GameScene) {}

  public openNpcInfoPanelFor(npcId: string): void {
    const npcState = this.scene.room?.state.npcs.get(npcId) as NPCStateView | undefined;
    if (!npcState) return;
    const templateId = npcState.templateId ?? '';
    if (!templateId) return;
    const info = buildNpcDisplayInfo(npcId, templateId, npcState.name);
    if (!info) return;
    if (!this.scene.npcInfoPanel) {
      this.createNpcInfoPanel();
    }
    this.scene.npcInfoPanel?.showNpcInfo(info);
  }

  /**
   * Right click sobre un NPC: route según el tipo de template:
   *  - vendor (merchantId / spellVendorId) o sacerdote (priestService) →
   *    INTERACT_NPC al server. Server valida distancia y devuelve
   *    MERCHANT_OPENED / SPELL_VENDOR_OPENED / NPC_DIALOGUE según corresponda,
   *    o "Estás muy lejos!" si está fuera de rango.
   *  - cualquier otro caso → NpcInfoPanel local (info de stats/loot derivada
   *    del template shared, sin server roundtrip).
   *
   * El left click ya cubre el "saludo" / chat bubble del message. El right
   * click es siempre la ACCIÓN principal del NPC.
   */
  public handleNpcRightClick(npcId: string): void {
    const npcState = this.scene.room?.state.npcs.get(npcId) as NPCStateView | undefined;
    if (!npcState) return;
    const templateId = npcState.templateId ?? '';
    const template = templateId ? getNPCTemplate(templateId) : undefined;

    // La ventanita de info (stats/loot) es SÓLO para NPCs HOSTILES (mobs). Cualquier
    // NPC NO hostil —vendors, servicios, custodio, party finder, reclutador/rey, o
    // un neutral sin rol— resuelve su interacción en el server (INTERACT_NPC), que
    // abre el panel/diálogo que corresponda. Regla: si no es hostil, no sale la
    // ventanita. Ver docs/VENDORS_AND_NPC_INTERACTION.md y docs/PARTY_SYSTEM.md.
    if (template && !template.hostile) {
      if (this.scene.room) {
        const interactMessage: InteractNpcMessage = {
          type: ClientMessageType.INTERACT_NPC,
          npcId,
        };
        this.scene.room.send(interactMessage.type, interactMessage);
      }
      return;
    }

    this.openNpcInfoPanelFor(npcId);
  }

  /**
   * Crea el NpcInfoPanel con posición persistida (igual que InvokePanel/ItemPanel).
   */
  public createNpcInfoPanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = (savedOptions as any)?.npcInfoPanelX ?? width / 2 + 200;
    const panelY = (savedOptions as any)?.npcInfoPanelY ?? height / 2;
    this.scene.npcInfoPanel = new NpcInfoPanel(
      this.scene,
      {
        onMove: (x, y) => this.scene.persistence.savePanelPosition('npcInfo', x, y),
      },
      {
        initialX: panelX,
        initialY: panelY,
        width: 280,
        height: 320,
      }
    );
  }


}
